// src/theme.ts
import { createTheme } from '@mui/material/styles';
import {
	ColorBaseCharcoal,
	ColorBaseGreenAbundant,
	ColorBaseGreenDark,
	ColorBaseGreenEnergy,
	ColorBaseGreyPastel,
	ColorBaseGreyLight,
	ColorBaseSageDeep,
	ColorBaseSageProsperous,
	ColorBaseSilverLight,
	ColorBaseTealDark,
	ColorBaseTealHighlight,
	ColorBaseTealPastel,
	ColorBaseWhite,
	ColorBaseMagentaDark,
	ColorBaseMagentaHighlight,
} from '@huntington/honeycomb-design/dist';
import { enUS } from '@mui/x-date-pickers/locales';

// override the Theme to include custom properties and MUI X DataGridPro support

export const chartColorWheel = [
	ColorBaseCharcoal,
	ColorBaseGreenAbundant,
	ColorBaseMagentaDark,
	ColorBaseTealHighlight,
	ColorBaseGreenEnergy,
	ColorBaseSageDeep,
	ColorBaseMagentaHighlight,
	ColorBaseGreyLight,
	ColorBaseTealPastel,
	ColorBaseGreenDark,
];
declare module '@mui/material/styles' {
	interface Theme {
		custom?: {
			chartColorWheel?: string[];
		};
	}
	interface ThemeOptions {
		custom?: {
			chartColorWheel?: string[];
		};
	}

	interface Palette {
		brand: {
			primary: string;
			secondary: string;
			accent: string;
			border: string;
			inversePrimary?: string;
		};
		semantic: {
			success: string;
			warning: string;
			error: string;
			info: string;
		};
		icons: {
			primary: string;
			secondary?: string;
		};
		surface: {
			primary: string;
			secondary: string;
			tertiary: string;
			overlay?: string;
		};
	}

	interface PaletteOptions {
		brand: {
			primary: string;
			secondary: string;
			accent: string;
			border: string;
			inversePrimary?: string;
		};
		semantic: {
			success: string;
			warning: string;
			error: string;
			info: string;
		};
		icons: {
			primary: string;
			secondary?: string;
		};
		surface: {
			primary: string;
			secondary: string;
			tertiary: string;
		};
	}
}
// @ts-ignore: MUI X DataGrid style override
declare module '@mui/material/styles/components' {
	interface Components {
		MuiDataGrid?: any;
	}
}

const mainTheme = createTheme(
	{
		breakpoints: {
			values: {
				xs: 0, // mobile
				sm: 600, // tablet portrait
				md: 960, // tablet landscape / small laptop
				lg: 1200, // desktop
				xl: 1536, // large screens
			},
		},
		palette: {
			primary: {
				main: ColorBaseSageProsperous,
			},
			secondary: {
				main: ColorBaseGreenAbundant,
			},
			background: {
				default: ColorBaseGreyPastel, // Main background color
				paper: ColorBaseWhite, // Background for Paper components (cards, dialogs, etc.)
			},
			brand: {
				primary: ColorBaseGreenEnergy,
				secondary: ColorBaseTealDark,
				inversePrimary: ColorBaseGreenAbundant,
				accent: ColorBaseSageDeep,
				border: ColorBaseCharcoal,
			},
			semantic: {
				success: ColorBaseSageProsperous,
				warning: '#FFFF00',
				error: '#D60000',
				info: '#0000FF',
			},
			icons: {
				primary: ColorBaseWhite,
			},
			surface: {
				primary: ColorBaseWhite,
				secondary: '#f5f5f5',
				tertiary: ColorBaseSilverLight,
			},
		},
		custom: {
			chartColorWheel,
		},
		typography: {
			fontFamily: '"Huntington Apex", Muli, Helvetica, Arial, sans-serif',
			h1: {
				fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif',
				color: ColorBaseCharcoal,
				fontSize: '2.5rem',
			},
			h2: { fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif', color: ColorBaseCharcoal },
			h3: {
				fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif',
				color: ColorBaseCharcoal,
				fontSize: '1.8575rem',
			},
			h4: { fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif', color: ColorBaseCharcoal },
			h5: { fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif', color: ColorBaseCharcoal },
			h6: {
				fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif',
				color: ColorBaseCharcoal,
				fontWeight: 'bold',
			},
			subtitle1: {
				fontFamily: 'Muli, Helvetica, Arial, sans-serif',
				color: ColorBaseCharcoal,
				fontSize: '0.8rem',
			},
			body1: {
				fontFamily: 'Muli, Helvetica, Arial, sans-serif',
				color: ColorBaseCharcoal,
				fontSize: '1rem',
			},
			body2: {
				fontFamily: 'Muli, Helvetica, Arial, sans-serif',
				color: ColorBaseGreenAbundant,
				fontSize: '1rem',
			},
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						backgroundColor: ColorBaseWhite,
					},
				},
			},
			// @ts-ignore: MUI X DataGrid style override
			MuiDataGrid: {
				styleOverrides: {
					root: {
						backgroundColor: ColorBaseWhite,
					},
					columnHeaders: {
						backgroundColor: ColorBaseWhite,
						boxShadow: 'none',
					},
					withBorderColor: {
						backgroundColor: ColorBaseWhite,
					},
					gridFiller: {
						backgroundColor: ColorBaseWhite,
					},
				},
			},
			ChartColorWheel: {
				styleOverrides: {
					root: {
						backgroundColor: ColorBaseWhite,
					},
				},
			},
		},
	},
	enUS
);

export const PRIVATE_LABEL_COLORS = {
	PrimaryBlue: '#002F49',
	SecondaryBlue: '#1E2E3B',
	TertiaryBlue: '#00313A',
	PrimaryContainer: '#044E73',
	SecondaryContainer: '#3B4B59',
	TertiaryContainer: '#00515E',
	InversePrimaryBlue: '#96CCF8',
};

const PRIVATE_LABEL_CHART_COLORS = [
	'#386287',
	'#6C95BB',
	'#2B6676',
	'#6099AB',
	'#59598E',
	'#8B8BC3',
	'#53606E',
	'#8593A0',
];

// Dark theme
export const privateLabelTheme = createTheme(
	{
		palette: {
			primary: {
				main: PRIVATE_LABEL_COLORS.PrimaryBlue,
			},
			secondary: {
				main: PRIVATE_LABEL_COLORS.PrimaryContainer,
			},
			background: {
				default: ColorBaseGreyPastel, // Main background color
				paper: ColorBaseWhite,
			},
			brand: {
				primary: '',
				secondary: PRIVATE_LABEL_COLORS.PrimaryContainer,
				inversePrimary: PRIVATE_LABEL_COLORS.InversePrimaryBlue,
				accent: '',
				border: '',
			},
			semantic: {
				success: '',
				warning: '',
				error: '',
				info: '',
			},
			icons: {
				primary: '',
				secondary: '',
			},
			surface: {
				primary: ColorBaseWhite,
				secondary: '#f5f5f5',
				tertiary: ColorBaseSilverLight,
			},
		},
		custom: {
			chartColorWheel: PRIVATE_LABEL_CHART_COLORS,
		},
		typography: {
			fontFamily: '"Huntington Apex", Muli, Helvetica, Arial, sans-serif',
			h1: {
				fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif',
				color: PRIVATE_LABEL_COLORS.PrimaryBlue,
				fontSize: '2.5rem',
			},
			h2: {
				fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif',
				color: PRIVATE_LABEL_COLORS.PrimaryBlue,
			},
			h3: {
				fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif',
				color: PRIVATE_LABEL_COLORS.PrimaryBlue,
				fontSize: '1.8575rem',
			},
			h4: {
				fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif',
				color: PRIVATE_LABEL_COLORS.PrimaryBlue,
			},
			h5: {
				fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif',
				color: PRIVATE_LABEL_COLORS.PrimaryBlue,
			},
			h6: {
				fontFamily: '"Huntington Apex", Helvetica, Arial, sans-serif',
				color: PRIVATE_LABEL_COLORS.PrimaryBlue,
				fontWeight: 'bold',
			},
			subtitle1: {
				fontFamily: 'Muli, Helvetica, Arial, sans-serif',
				color: PRIVATE_LABEL_COLORS.PrimaryBlue,
				fontSize: '0.8rem',
			},
			body1: {
				fontFamily: 'Muli, Helvetica, Arial, sans-serif',
				color: PRIVATE_LABEL_COLORS.PrimaryBlue,
				fontSize: '1rem',
			},
			body2: {
				fontFamily: 'Muli, Helvetica, Arial, sans-serif',
				color: PRIVATE_LABEL_COLORS.PrimaryBlue,
				fontSize: '1rem',
			},
		},
	},
	enUS
);

export const themes = {
	main: mainTheme,
	privateLabel: privateLabelTheme,
};
